import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import { getBrochureForPrint } from "../store/service";
import "./ProductsList.css";
import "./BrochurePrint.css";
import { getStoreLogoPath } from "../utils";

/* Shared 3-column spec layout so dimensions row and Backer/Material/Assembly row align */
const SPEC_ROW_STYLE = { gap: "12px" };
const SPEC_COL_STYLE = { flex: 1, minWidth: 0 };
const materialMapper = {
  bwp: "BWP",
  mr: "MR",
};

const getDimensionValues = (min, max, step) => {
  const minN = Number(min);
  const maxN = Number(max);
  const stepN = Number(step);
  if (
    Number.isNaN(minN) ||
    Number.isNaN(maxN) ||
    Number.isNaN(stepN) ||
    stepN <= 0
  )
    return [];

  const values = [];
  for (let v = minN; v <= maxN; v += stepN) values.push(v);
  return values;
};

const renderDimensions = (product) => {
  const cfg = product?.dimensions_config;

  if (!cfg) return null;

  const dimensions = ["height", "width", "depth"];

  return (
    <>
      <div className="d-flex text-muted my-2" style={SPEC_ROW_STYLE}>
        {dimensions.map((label) => {
          const min = cfg[`min_${label}`];
          const max = cfg[`max_${label}`];
          const step = cfg[`${label}_step`];
          const values = getDimensionValues(min, max, step);
          const useList = values.length > 0 && values.length <= 4;

          return (
            <div
              key={label}
              className="d-flex flex-column gap-1"
              style={SPEC_COL_STYLE}
            >
              <span style={{ fontSize: "8px" }}>
                {label[0].toUpperCase() + label.slice(1)} (mm)
              </span>
              <span style={{ fontSize: "8px" }} className="fw-bold">
                {useList ? values.join(" / ") : `${min} - ${max}`}
              </span>
              {!useList && (
                <span style={{ fontSize: "8px" }}>(every {step}mm)</span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

const renderRangeInfo = (baseCode, material) => {
  const base = typeof baseCode === "string" ? baseCode : "";
  const rows = [
    { range: "Eco", code: `${base}-E` },
    { range: "Prime", code: `${base}-P` },
    { range: "Gold", code: `${base}-D` },
  ];

  return (
    <>
      <table
        className="w-100 mt-2"
        style={{
          fontSize: "8px",
          borderCollapse: "collapse",
          border: "1px solid #e9ecef",
          color: "var(--bs-secondary-color, #6c757d)",
        }}
      >
        <thead className="bg-light">
          <tr>
            <th
              className="column-style text-uppercase"
              style={{ width: "10mm" }}
            >
              Range
            </th>
            <th
              className="column-style text-uppercase"
              style={{ width: "10mm" }}
            >
              Material
            </th>
            <th
              className="column-style text-uppercase"
              style={{ width: "13mm" }}
            >
              Base Code
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.range}>
              <td
                className="row-style"
                style={{ width: "10mm", fontWeight: 600 }}
              >
                {r.range}
              </td>
              <td
                className="row-style"
                style={{ width: "10mm", fontWeight: 600 }}
              >
                {`${materialMapper[material.split("_")[1]]} Plywood`}
              </td>
              <td
                className="row-style"
                style={{ width: "13mm", fontWeight: 700 }}
              >
                {r.code}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <span className="text-muted d-block mt-1" style={{ fontSize: "8px" }}>
        For range details, see page 2
      </span>
    </>
  );
};

const renderProduct = (product) => {
  const images = Array.isArray(product.images) ? product.images : [];
  const mainImage = images[0];
  const secondaryImage = images[1];

  return (
    <div
      className="d-flex flex-column justify-content-between bg-white"
      style={{
        height: "86mm",
        padding: "5mm 5mm 2.5mm 5mm",
      }}
    >
      <div className="d-flex" style={{ gap: "5mm" }}>
        <div
          className="d-flex flex-column"
          style={{
            width: "46mm",
            flex: "0 0 46mm",
            alignSelf: "stretch",
            minHeight: 0,
          }}
        >
          <div className="d-flex flex-column" style={{ gap: "5mm" }}>
            <img
              src={mainImage}
              alt={product.name}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "35mm",
                display: "block",
                objectFit: "contain",
              }}
            />
            {secondaryImage && (
              <img
                src={secondaryImage}
                alt={`${product.name} secondary`}
                style={{
                  width: "20.5mm",
                  height: "17.5mm",
                }}
              />
            )}
          </div>
          <span
            className="text-muted lh-1"
            style={{
              fontSize: "8px",
              maxWidth: "46mm",
              marginTop: "auto",
            }}
          >
            Shutters shown for representation only
          </span>
        </div>

        <div className="d-flex flex-column w-100">
          <h1
            className="fw-bold mb-0"
            style={{
              fontSize: "15px",
              lineHeight: "1",
              color: "#f28c28",
            }}
          >
            {product.name}
          </h1>
          <div
            className="text-muted lh-1 fw-bold mt-1"
            style={{ fontSize: "10px" }}
          >
            {product.base_code}
          </div>
          <hr className="mt-1 mb-0" />
          {renderDimensions(product)}
          {renderRangeInfo(product.base_code, product.material)}
        </div>
      </div>
      <div>
        <hr className="m-0" />

        <div
          className="d-flex justify-content-between align-items-center"
          style={{ gap: "6px", marginTop: "2.5mm" }}
        >
          <span style={{ fontSize: "10px", color: "#D97706" }}>
            <i className="fa fa-truck me-1" aria-hidden="true" />3 day delivery
          </span>
          <span
            className="text-muted text-end lh-1"
            style={{ fontSize: "10px" }}
          >
            Includes all hardwares & accessories except plinth
          </span>
        </div>
      </div>
    </div>
  );
};

const renderProductsGroup = (products, pageNumber) => {
  return (
    <div
      className="brochure-print-page py-4 d-flex flex-column"
      style={{ minHeight: "694px" }}
    >
      <div className="d-flex justify-content-center">
        <img
          src={getStoreLogoPath()}
          alt="Zerobuild Logo"
          style={{ height: "8mm", objectFit: "contain" }}
        />
      </div>
      <div
        className="d-flex flex-column"
        style={{ gap: "5mm", marginTop: "3.5mm", marginBottom: "5.5mm" }}
      >
        {products.map((product) => renderProduct(product))}
      </div>
      <div className="d-flex justify-content-between fw-bold mt-auto">
        <span className="text-muted text-uppercase">zerobuild.in</span>
        <span className="text-muted">{pageNumber}</span>
      </div>
    </div>
  );
};

const renderBanner = (banner) => {
  const hasImage = !!banner?.image;

  return (
    <div className="brochure-print-banner-full brochure-print-banner">
      {hasImage ? (
        <img
          src={banner.image}
          alt="Banner"
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : null}
    </div>
  );
};

const BrochurePrint = () => {
  const [searchParams] = useSearchParams();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBrochure();

    const style = document.createElement("style");
    style.innerHTML = `
      @media print {
        @page {
          size: 148mm 210mm;
          margin: 0;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [searchParams]);

  const fetchBrochure = async () => {
    try {
      const response = await getBrochureForPrint();
      if (Array.isArray(response)) {
        setPages(response);
      } else if (Array.isArray(response?.pages)) {
        setPages(response.pages);
      } else {
        setPages([]);
      }
    } catch (err) {
      setError(err?.error || err?.message || "Failed to fetch brochure");
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="catalog-products-list-container">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner color="primary" />
        </div>
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="brochure-print-print-root price-print-view d-flex flex-column">
        {pages.map((page, index) => {
          const pageNumber = index + 1;
          if (page.type === "banner") {
            return (
              <React.Fragment key={`banner-${index}`}>
                {renderBanner(page)}
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={`products-${index}`}>
              {renderProductsGroup(page.products, pageNumber)}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};

export default BrochurePrint;
